import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { BadgeCheck, Check, CreditCard, ReceiptText, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { CartView, PaymentPreference } from '@/lib/draft-order-client';
import type { DraftWorkingCopy } from './cart-draft';

interface CartPaymentSectionProps {
  copy: DraftWorkingCopy;
  cart: CartView;
  editable: boolean;
  /** True while the manual transfer confirmation is being written or undone. */
  transferBusy: boolean;
  onChange: (patch: Partial<DraftWorkingCopy>) => void;
  /** Confirm the transfer by hand — the payment a human verified outside this system. */
  onConfirmTransfer: (reason: string) => void;
  onRevertTransfer: () => void;
}

export function CartPaymentSection({
  copy,
  cart,
  editable,
  transferBusy,
  onChange,
  onConfirmTransfer,
  onRevertTransfer,
}: CartPaymentSectionProps) {
  const { t } = useTranslation('conversations');
  const methods = cart.payment?.availablePaymentMethods ?? [];
  const storedCard = cart.payment?.storedCard;
  const manual = cart.payment?.transferManualVerification;

  // Read off the SAVED cart, never the staged copy: the confirmation acts on the server immediately
  // (like coupons), so offering it against a locally-picked method would vouch for a payment the
  // order does not yet record. Picking "transferencia" and pressing Guardar is what unlocks it.
  const isTransferOrder = cart.paymentPreference === ('bank_transfer' satisfies PaymentPreference);
  // A receipt the reader already cleared needs nobody's word for it — offering the override there
  // would only invite one where it buys nothing. Everything else does: no proof at all, or one the
  // reader left pending or mismatched, which is precisely what a human resolves by looking at it.
  const needsHumanVerdict = !cart.payment?.transferStatus || cart.payment.transferStatus !== 'verified';

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('cart.payment.title')}</h3>

      {methods.length === 0 && <p className="text-sm text-muted-foreground">{t('cart.payment.none')}</p>}

      <div className="grid gap-1.5">
        {methods.map((method) => {
          const active = copy.paymentPreference === method;
          return (
            <button
              key={method}
              type="button"
              disabled={!editable}
              onClick={() => onChange({ paymentPreference: method, useSavedCard: false })}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                active ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-background text-foreground',
                editable && !active && 'hover:bg-accent',
                !editable && 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  active ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                )}
              >
                {active && <Check className="h-3 w-3" />}
              </span>
              {t(`cart.payment.methods.${method}` as const, { defaultValue: method })}
            </button>
          );
        })}
      </div>

      {/* A saved card is never charged unless it was explicitly chosen — the same consent rule the
          agent follows, surfaced as a separate deliberate choice rather than implied by "card". */}
      {storedCard && copy.paymentPreference === ('card' satisfies PaymentPreference) && (
        <button
          type="button"
          disabled={!editable}
          onClick={() => onChange({ useSavedCard: !copy.useSavedCard })}
          className={cn(
            'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
            copy.useSavedCard
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border bg-background text-foreground',
            editable && !copy.useSavedCard && 'hover:bg-accent',
            !editable && 'cursor-default',
          )}
        >
          <CreditCard className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">{t('cart.payment.useSavedCard', { label: storedCard.label })}</span>
          {copy.useSavedCard && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
        </button>
      )}

      {/* The FILE, not the settled flag: a manual confirmation settles the payment with nothing
          attached, and the panel must not claim a comprobante it cannot open. */}
      {cart.payment?.transferReceiptFile && (
        <p className="flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300">
          <ReceiptText className="h-3.5 w-3.5 shrink-0" />
          {t('cart.payment.receiptAttached')}
        </p>
      )}

      {isTransferOrder &&
        (manual ? (
          <ManualTransferNotice manual={manual} editable={editable} busy={transferBusy} onRevert={onRevertTransfer} />
        ) : needsHumanVerdict ? (
          <ConfirmTransferAction
            /* Offered whether or not a receipt is attached: the one the reader could not match is
               exactly the case an operator resolves by looking at it themselves. */
            hasReceipt={Boolean(cart.payment?.transferReceiptFile)}
            editable={editable}
            busy={transferBusy}
            onConfirm={onConfirmTransfer}
          />
        ) : null)}
    </section>
  );
}

/**
 * The manual confirmation, once made — deliberately loud and permanently visible.
 *
 * This is the one payment on an order that no document backs, so the panel says who settled it and
 * when for as long as the cart is open, rather than quietly turning into a generic "paid" state.
 */
function ManualTransferNotice({
  manual,
  editable,
  busy,
  onRevert,
}: {
  manual: NonNullable<NonNullable<CartView['payment']>['transferManualVerification']>;
  editable: boolean;
  busy: boolean;
  onRevert: () => void;
}) {
  const { t } = useTranslation('conversations');

  return (
    <div className="space-y-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300">
      <p className="flex items-center gap-1.5 font-medium">
        <BadgeCheck className="h-3.5 w-3.5 shrink-0" />
        {t('cart.payment.transferOverride.confirmed')}
      </p>
      <p className="text-emerald-700/90 dark:text-emerald-300/80">
        {t(manual.hadReceipt ? 'cart.payment.transferOverride.overReceipt' : 'cart.payment.transferOverride.noReceipt')}
        {' · '}
        {format(new Date(manual.at), 'dd/MM HH:mm')}
      </p>
      {manual.reason && (
        <p className="break-words italic text-emerald-700/90 dark:text-emerald-300/80">“{manual.reason}”</p>
      )}
      {editable && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-emerald-800 hover:bg-emerald-100 dark:text-emerald-300 dark:hover:bg-emerald-900/40"
          loading={busy}
          onClick={onRevert}
        >
          {t('cart.payment.transferOverride.revert')}
        </Button>
      )}
    </div>
  );
}

/** The way out of `TRANSFER_SCREENSHOT_MISSING` when the operator, not the agent, saw the money. */
function ConfirmTransferAction({
  hasReceipt,
  editable,
  busy,
  onConfirm,
}: {
  hasReceipt: boolean;
  editable: boolean;
  busy: boolean;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation('conversations');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const submit = () => {
    onConfirm(reason.trim());
    setOpen(false);
    setReason('');
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        disabled={!editable}
        loading={busy}
        onClick={() => setOpen(true)}
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        {t('cart.payment.transferOverride.action')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('cart.payment.transferOverride.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {t(
                hasReceipt
                  ? 'cart.payment.transferOverride.dialogBodyWithReceipt'
                  : 'cart.payment.transferOverride.dialogBody',
              )}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('cart.payment.transferOverride.reasonPlaceholder')}
            maxLength={2000}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('cart.payment.transferOverride.cancel')}
            </Button>
            <Button onClick={submit}>{t('cart.payment.transferOverride.submit')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
